import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { SettingsSection } from "@/components/SettingsSection";
import { Button } from "@/components/ui/Button";
import { useInvoicesQuery } from "@/features/billing/hooks/useBilling";
import type { Invoice } from "@/features/billing/types";

function invoiceStatusLabel(status: string, t: TFunction): string {
  switch (status) {
    case "paid":
      return t("billing.invoices.status.paid");
    case "open":
      return t("billing.invoices.status.open");
    case "void":
      return t("billing.invoices.status.void");
    case "uncollectible":
      return t("billing.invoices.status.uncollectible");
    case "draft":
      return t("billing.invoices.status.draft");
    default:
      return status;
  }
}

function formatAmount(invoice: Invoice, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: invoice.currency.toUpperCase(),
  }).format(invoice.amountPaid / 100);
}

export function InvoicesSection() {
  const { t, i18n } = useTranslation();
  const { data: invoices, isLoading } = useInvoicesQuery();

  return (
    <SettingsSection title={t("billing.invoices.title")}>
      {isLoading || !invoices ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">{t("common.loading")}</div>
      ) : invoices.length === 0 ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">{t("billing.invoices.empty")}</div>
      ) : (
        invoices.map((invoice) => (
          <div key={invoice.id} className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {new Date(invoice.created).toLocaleDateString(i18n.language)} ·{" "}
                {formatAmount(invoice, i18n.language)}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {invoiceStatusLabel(invoice.status, t)}
              </p>
            </div>
            <div className="shrink-0">
              {invoice.invoicePdf && (
                <Button
                  size="sm"
                  variant="secondary"
                  render={<a href={invoice.invoicePdf} target="_blank" rel="noreferrer" />}
                >
                  {t("billing.invoices.download")}
                </Button>
              )}
            </div>
          </div>
        ))
      )}
    </SettingsSection>
  );
}
